import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { getItemsTableConfig } from '../../lib-items'
import { calculateItemsTableTotals } from '../../lib/calculate-items-table-totals'
import { Item, OptionDetails, Options, ProductsService } from '~/shared/api'
import Spinner from '~/shared/ui/spinner'
import { CustomSelect, Empty } from '~/shared/ui'
import { TableContainer } from '~/shared/ui/table'
import { useStore } from '~/app/store'
import { useErrorHandler } from '~/shared/lib/hooks/use-error-handler'
import { SVGPrint } from '~/shared/ui/SVG/SVGPrint'
import { ModalBarcodePrinting } from '~/features/products/crud/product-general/Modal-barcode-printing'

export const SubRowComponentFirst = observer((props: { id: string }) => {
  const { productsStore } = useStore()
  const { getItems, productsSubContentOpens } = productsStore
  const { handleErrorWithToast } = useErrorHandler()
  const [items, setItems] = useState<undefined | Item[]>(undefined)
  const [options, setOptions] = useState<undefined | Options>(undefined)
  const [detailOptions, setDetailOptions] = useState<
    undefined | OptionDetails[]
  >(undefined)
  const [barcodeOptions, setBarcodeOptions] = useState<OptionDetails[]>([])
  const [loading, setLoading] = useState(false)
  const [barcodeOptionsLoading, setBarcodeOptionsLoading] = useState(false)

  const [openModal, setOpenModal] = useState(false)

  const [selectOptions, setSelectOptions] = useState<{
    [key: string]: { id: string; label: string }
  }>({})

  const visibleItems = useMemo(() => {
    if (!items) return []

    const selectedOptionValueIds = Object.values(selectOptions)
      .map((selectOption) => selectOption.id)
      .filter(Boolean)

    if (!selectedOptionValueIds.length) return items

    return items.filter((item) =>
      selectedOptionValueIds.every((optionValueId) =>
        (item.optionValues ?? []).includes(optionValueId),
      ),
    )
  }, [items, selectOptions])

  const totals = useMemo(
    () => calculateItemsTableTotals(visibleItems),
    [visibleItems],
  )

  const columns = useMemo(
    () => getItemsTableConfig(visibleItems.length > 1 ? totals : undefined),
    [totals, visibleItems.length],
  )

  const loadData = useCallback(() => {
    setLoading(true)
    getItems(props.id)
      .then((data) => {
        setItems(data?.items)
        setOptions(data?.options)
        setDetailOptions(data?.detailOptions)
      })
      .catch(handleErrorWithToast)
      .finally(() => setLoading(false))
  }, [getItems, handleErrorWithToast, props.id])

  useEffect(() => {
    if (
      productsSubContentOpens.includes(props.id) &&
      [items, options, detailOptions].every((arrData) => !arrData)
    ) {
      loadData()
    }
  }, [
    productsSubContentOpens,
    props.id,
    items,
    options,
    detailOptions,
    loadData,
  ])

  useEffect(() => {
    if (productsSubContentOpens.includes(props.id)) {
      const interval = setInterval(() => {
        loadData()
      }, 300000)

      return () => clearInterval(interval)
    }
  }, [productsSubContentOpens, props.id, loadData])

  const handleOpenBarcodeModal = async (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    try {
      setBarcodeOptionsLoading(true)
      const { data } = await ProductsService.getOptionsDetail(props.id)
      setBarcodeOptions(data.options)
      setOpenModal(true)
    } catch (error) {
      handleErrorWithToast(error)
    } finally {
      setBarcodeOptionsLoading(false)
    }
  }

  if (!productsSubContentOpens.includes(props.id)) {
    return null
  }

  if (loading) {
    return (
      <div className="flex justify-center my-5">
        <Spinner />
      </div>
    )
  }

  if (!items || !items.length) {
    return (
      <div className="flex justify-center my-5">
        <Empty />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between mb-4">
        <div className="flex flex-wrap gap-3">
          {options &&
            Object.keys(options).map((option) => {
              const currentOptions = (options[option] ?? []).map((opt) => ({
                value: opt.id,
                label: opt.name,
              }))
              const selected = selectOptions[option]?.id
                ? {
                    value: selectOptions[option].id,
                    label: selectOptions[option].label,
                  }
                : null
              return (
                <div key={option}>
                  <CustomSelect
                    labelType="floating"
                    placeholder={option}
                    value={selected}
                    options={currentOptions}
                    key={option}
                    className="w-[200px]"
                    isClearable
                    onChange={(newValue: unknown | null) => {
                      const selectOption = newValue as {
                        label: string
                        value: string
                      }
                      setSelectOptions((prev) => ({
                        ...prev,
                        [option]: {
                          id: selectOption?.value || '',
                          label: selectOption?.label || '',
                        },
                      }))
                    }}
                  />
                </div>
              )
            })}
        </div>

        <div onClick={handleOpenBarcodeModal} className="cursor-pointer">
          {barcodeOptionsLoading ? (
            <Spinner className="w-7 h-7" />
          ) : (
            <SVGPrint size="30" />
          )}
        </div>
      </div>
      <div className="w-full border border-[#E0E0E0] rounded-t-xl rounded-b-md">
        <TableContainer
          data={visibleItems}
          aroundwithout
          variant="default"
          columns={columns}
          alignBodyStyle="align-top"
          className="w-full"
        />
      </div>
      <ModalBarcodePrinting
        id={props.id}
        isOpen={openModal}
        onClose={(e) => {
          e.preventDefault()
          setOpenModal(false)
        }}
        options={barcodeOptions.length ? barcodeOptions : detailOptions}
      />
    </div>
  )
})
